import { heightAt, WATER_LEVEL } from "./terrain.ts";
import type { MapDot } from "./world.ts";
import { currentWaypoint } from "./quest.ts";
import { questById } from "./quest/index.ts";
import { liveById } from "./npc/index.ts";
import { game } from "./state.ts";
import { ACTIVE_MAP_ID } from "./map/active.ts";

// A lightweight 2D top-down minimap drawn on a Canvas overlay.
// The terrain + static deco dots are baked once into an offscreen canvas;
// player, remotes, and quest waypoints are redrawn each tick.

const RES = 320; // internal resolution (px); CSS scales it down for crispness
let view: CanvasRenderingContext2D;
let base: HTMLCanvasElement;
let world = 1;
let el: HTMLCanvasElement;

/** Toggle the maximized (large, centered) view of the minimap. */
export function toggleMinimapMax(): void {
  el?.classList.toggle("maximized");
}

/** World (x,z) → minimap pixel (+z points down/south). */
function toMap(x: number, z: number): [number, number] {
  return [(x / world + 0.5) * RES, (z / world + 0.5) * RES];
}

export function initMinimap(canvas: HTMLCanvasElement, dots: MapDot[], worldSize: number): void {
  canvas.width = RES;
  canvas.height = RES;
  world = worldSize;
  el = canvas;
  view = canvas.getContext("2d")!;

  base = document.createElement("canvas");
  base.width = RES;
  base.height = RES;
  const b = base.getContext("2d")!;

  // This is a *landmark* map, not a biome map: land reads as one muted base tone so
  // the three things that matter — bodies of water, roads, and structures — stand
  // out. Bake water/land by sampling the heightmap on a grid.
  const DEEP = "#2f5d8a";   // open water
  const SHALLOW = "#5b87b0"; // shoreline / shallows
  const LAND = "#7c8a63";   // flat muted land
  // Bake sample grid — ~4.5 world-units per cell (240 at size 1080), capped at
  // 320 so a 2160 map costs ~100k heightAt samples once instead of 4×.
  const N = Math.min(320, Math.max(120, Math.round(worldSize / 4.5)));
  const cell = RES / N;
  for (let ix = 0; ix < N; ix++) {
    for (let iz = 0; iz < N; iz++) {
      const wx = ((ix + 0.5) / N - 0.5) * world;
      const wz = ((iz + 0.5) / N - 0.5) * world;
      const y = heightAt(wx, wz);
      b.fillStyle = y < WATER_LEVEL ? DEEP : y < WATER_LEVEL + 0.5 ? SHALLOW : LAND;
      b.fillRect(ix * cell, iz * cell, cell + 1, cell + 1);
    }
  }

  // Roads: lay a continuous tan ribbon so they read as a route, not scattered pips.
  const roads = dots.filter((d) => d.kind === "road");
  if (roads.length) {
    b.strokeStyle = "#cbb784";
    b.lineCap = "round";
    b.lineJoin = "round";
    b.lineWidth = 3.2;
    b.beginPath();
    roads.forEach((d, i) => {
      const [mx, my] = toMap(d.x, d.z);
      i === 0 ? b.moveTo(mx, my) : b.lineTo(mx, my);
    });
    b.stroke();
  }

  // Structures: a bold, dark-outlined square per footprint dot, in its own color.
  for (const d of dots) {
    if (d.kind !== "structure") continue; // skip deco scatter entirely — it's clutter here
    const [mx, my] = toMap(d.x, d.z);
    const s = 4.5;
    b.fillStyle = "#" + d.color.toString(16).padStart(6, "0");
    b.strokeStyle = "#1c1c1c";
    b.lineWidth = 1;
    b.fillRect(mx - s / 2, my - s / 2, s, s);
    b.strokeRect(mx - s / 2, my - s / 2, s, s);
  }
}

type RemoteDot = { x: number; z: number; color: number };

// Optional debug overlay drawn on top of everything each frame (grid cells +
// interest rings — see src/imdebug.ts). Registered/cleared on HUD toggle.
export type MinimapOverlay = (
  ctx: CanvasRenderingContext2D,
  toMap: (x: number, z: number) => [number, number],
  res: number,
  worldSize: number,
) => void;
let overlay: MinimapOverlay | null = null;
export function setMinimapOverlay(fn: MinimapOverlay | null): void {
  overlay = fn;
}

let lastDraw = 0;
export function updateMinimap(
  px: number,
  pz: number,
  heading: number,
  remotes: RemoteDot[] = [],
): void {
  if (!view) return;
  // ~20Hz is indistinguishable for a top-down map but drops 2/3 of the full
  // clear + 320×320 blit + per-dot redraws the old every-frame cadence paid.
  const now = performance.now();
  if (now - lastDraw < 50) return;
  lastDraw = now;
  view.clearRect(0, 0, RES, RES);
  view.drawImage(base, 0, 0);

  // Remote players: a filled dot in their shirt color with a dark outline so it
  // reads against any terrain. Drawn under the local arrow.
  for (const r of remotes) {
    const [rx, ry] = toMap(r.x, r.z);
    view.beginPath();
    view.arc(rx, ry, 4, 0, Math.PI * 2);
    view.fillStyle = "#" + (r.color >>> 0).toString(16).padStart(6, "0");
    view.strokeStyle = "#1a1a1a";
    view.lineWidth = 1.5;
    view.fill();
    view.stroke();
  }

  // Quest waypoints (gold diamond): live objective NPC if present on this map,
  // else a coach fixed point (e.g. south-road edge before a cross-map kill).
  if (game.joined) {
    for (const w of questMinimapWaypoints()) {
      drawQuestWaypoint(w.x, w.z);
    }
  }

  const [mx, my] = toMap(px, pz);
  const fx = Math.sin(heading), fz = Math.cos(heading); // forward dir on map
  const px2 = fz, pz2 = -fx;                            // right perpendicular
  const tip = 9, back = 6, wide = 5;

  view.beginPath();
  view.moveTo(mx + fx * tip, my + fz * tip);                       // nose
  view.lineTo(mx - fx * back + px2 * wide, my - fz * back + pz2 * wide); // left
  view.lineTo(mx - fx * back - px2 * wide, my - fz * back - pz2 * wide); // right
  view.closePath();
  view.fillStyle = "#ffe08a";
  view.strokeStyle = "#3a2a10";
  view.lineWidth = 1.5;
  view.fill();
  view.stroke();

  if (overlay) overlay(view, toMap, RES, world);
}

/** Gold diamond matching the quest tracker hue — route marker on the map. */
function drawQuestWaypoint(x: number, z: number): void {
  let [mx, my] = toMap(x, z);
  // Skip far off-map coords (e.g. stale coach points after a transfer).
  if (mx < -RES || mx > RES * 2 || my < -RES || my > RES * 2) return;

  // The HUD minimap is a CIRCLE (`border-radius: 50%`); the canvas is square.
  // Corners (and south/north rim points near the edge of the full map) get
  // clipped. When not maximized, clamp the marker to the visible disc so a
  // "south road" target still reads as a rim diamond instead of vanishing.
  const maximized = !!el?.classList.contains("maximized");
  if (!maximized) {
    const cx = RES / 2, cy = RES / 2;
    // Stay inside the white border ring (~3px CSS → ~RES/2 − a few canvas px).
    const r = RES / 2 - 14;
    const dx = mx - cx, dy = my - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > r && dist > 1e-6) {
      const t = r / dist;
      mx = cx + dx * t;
      my = cy + dy * t;
    }
  }

  // Sized to stay readable when the 320 canvas is CSS-scaled to the 170px HUD.
  const s = maximized ? 7 : 9;
  view.beginPath();
  view.moveTo(mx, my - s);
  view.lineTo(mx + s, my);
  view.lineTo(mx, my + s);
  view.lineTo(mx - s, my);
  view.closePath();
  view.fillStyle = "#ffd86a";
  view.strokeStyle = "#3a2a10";
  view.lineWidth = 2;
  view.fill();
  view.stroke();
}

/**
 * Prefer the live objective NPC when it's on this map; otherwise use a fixed
 * coach `point` so cross-map steps (south road → tutorial-south wolves) still
 * show a route on the minimap.
 */
function questMinimapWaypoints(): { x: number; z: number }[] {
  const wp = currentWaypoint();
  if (!wp) return [];
  if (wp.targetNpcId) {
    const L = liveById(wp.targetNpcId);
    if (L && !L.defeated && !L.netHidden) {
      return [{ x: L.group.position.x, z: L.group.position.z }];
    }
  }
  const step = questById(wp.questId)?.steps.find((s) => s.id === wp.stepId);
  const pt = step?.coach?.point;
  // Map-scoped coach points only draw on their authored map (same rule as coach.ts).
  if (pt && (!pt.map || pt.map === ACTIVE_MAP_ID)) return [{ x: pt.x, z: pt.z }];
  return [];
}
